import type { NextPage, GetStaticProps } from 'next'
import Head from 'next/head'
import { mediaClient } from 'src/apiMiddleware'
import ArticleList, { Props } from '@/containers/article/List'
import { Params } from 'src/types'
import { range, contentModify } from '@/utils'
import { ArticleTag } from '@/api/types'
import { ArticleDetailBase, CategoryPageBase } from '@/route'

type StaticProps = Pick<Props, 'articles' | 'page'> & {
  categoryName: string
  categoryId: string
  tags: ArticleTag[]
}

const ALL_CATEGORY = 1000
const PER_PAGE = 12
const totalPage = (totalCount: number): number => {
  return Math.ceil(totalCount / PER_PAGE)
}

export async function getStaticPaths() {
  const categories = await mediaClient.categories.$get({ query: { limit: ALL_CATEGORY } })

  const aPromises = categories.contents.map((c) => {
    return mediaClient.articles.$get({ query: { limit: 0, offset: 0, filters: `category[equals]${c.id}` } })
  })

  const aWithCategories = await Promise.all(aPromises)

  const ps = aWithCategories
    .map((ac, idx) => {
      const lastPageNum = totalPage(ac.totalCount)
      const paths = range(1, lastPageNum).map((i) => {
        return {
          params: { id: categories.contents[idx].id, pageId: i.toString() },
        }
      })
      return paths
    })
    .flat()

  return {
    paths: ps,
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps<StaticProps, Params> = async ({ params }) => {
  const numID = Number(params?.pageId)
  if (isNaN(numID)) {
    return {
      props: {},
      notFound: true,
    }
  }

  const categoryId = params?.id
  if (!categoryId) {
    return {
      props: {},
      notFound: true,
    }
  }

  const offset = (numID - 1) * PER_PAGE
  const promise = mediaClient.articles.$get({
    query: { limit: PER_PAGE, offset, filters: `category[equals]${categoryId}` },
  })
  const tagPromise = mediaClient.tags.$get({ query: { limit: 1000 } })

  const [data, tags] = await Promise.all([promise, tagPromise])
  const page = { current: numID, total: totalPage(data.totalCount) }

  const categoryName = data.contents[0].category.name
  const articles = contentModify(data.contents, ArticleDetailBase)

  return {
    props: { articles, page, categoryName, categoryId, tags: tags.contents },
  }
}

const Pages: NextPage<StaticProps> = ({ articles, page, categoryName, categoryId }) => {
  const breadcrumbs = [{ title: 'Top', link: '/' }, { title: categoryName }]

  const generatePath = (id: string) => {
    return CategoryPageBase(categoryId, id)
  }
  return (
    <>
      <Head>
        <title key="title">【カテゴリー】{categoryName}一覧｜IoTBiz｜DXHUB株式会社</title>
        <meta property="og:url" content={`https://iot.dxhub.co.jp/categories/${page.current}/${categoryId}`} key="ou" />
        <meta property="og:title" content={categoryName + '一覧｜IoTBiz｜DXHUB株式会社'} key="ot" />
      </Head>
      <ArticleList articles={articles} page={page} breadcrumbs={breadcrumbs} title={categoryName} path={generatePath}/>
    </>
  )
}

export default Pages
